import './OfferExpiryNoticeStyles.less';
import './OfferLayoutStyles.less';

import { Icon } from '@opendoor/bricks';
import classNames from 'classnames';
import { reduce } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { createSelector } from 'reselect';

import Layout, { IProps as ILayoutProps } from '../../components/common/Layout';
import { IBrokerageInfo } from '../../declarations';
import { requestNewOffer, setInvitationToken } from '../../store/brokerageOfferFlow/actions';
import * as brokerageOfferFlowSelectors from '../../store/brokerageOfferFlow/selectors';

export interface IProps {
  hasDeclineReason: boolean;
  history?: any;
  leadEmail: string;
  leadFirstName: string;
  location?: any;
  hasOfferDataLoaded: boolean;
  isExpired: boolean;
  isNewOfferRequested: boolean;
  match?: any;
  onPrimaryBtnClick?: () => void;
  onPrevBtnClick?: () => void;
  onSecondaryBtnClick?: () => void;
  prevBtnText?: string;
  prevBtnUrl?: string;
  primaryBtnDisabled?: boolean;
  primaryBtnText?: string;
  primaryBtnUrl?: string;
  requestNewOffer?: () => void;
  secondaryBtnText?: string;
  secondaryBtnUrl?: string;
  setInvitationToken?: (invitationToken: string, brokerageOfferToken: string) => void;
  brokerageInfo?: IBrokerageInfo;
  headerType?: ILayoutProps['headerType'];
  footerType?: ILayoutProps['footerType'];
  disclaimerType?: ILayoutProps['disclaimerType'];
}

export class OfferLayout extends React.Component<IProps> {
  public static defaultProps: Partial<IProps> = {
    isNewOfferRequested: false,
  };

  public componentDidMount() {
    this.handleInvitationToken();
  }

  public render() {
    return (
      <div className="od-offer-layout-wrapper">
        <Layout
          brokerageInfo={this.props.brokerageInfo}
          footerType={this.props.footerType}
          headerType={this.props.headerType}
          disclaimerType={this.props.disclaimerType}
        >
          {this.renderExpiryBanner()}
          {this.props.children}
          {this.renderFooter()}
        </Layout>
      </div>
    );
  }

  private onPrevClick = () => {
    const { history, prevBtnUrl, onPrevBtnClick } = this.props;

    if (onPrevBtnClick) {
      onPrevBtnClick();
    }
    if (prevBtnUrl) {
      history.push(prevBtnUrl);
    }
  };

  private onSecondaryClick = () => {
    const { history, secondaryBtnUrl, onSecondaryBtnClick } = this.props;

    if (onSecondaryBtnClick) {
      onSecondaryBtnClick();
    }
    if (secondaryBtnUrl) {
      history.push(secondaryBtnUrl);
    }
  };

  private onNextClick = () => {
    const { history, primaryBtnUrl, onPrimaryBtnClick } = this.props;

    if (onPrimaryBtnClick) {
      onPrimaryBtnClick();
    }
    if (primaryBtnUrl) {
      history.push(primaryBtnUrl);
    }
  };

  private renderExpiryBanner() {
    const { hasOfferDataLoaded, isExpired, isNewOfferRequested } = this.props;
    let banner = null;

    if (!hasOfferDataLoaded || !isExpired) {
      return null;
    }
    if (isNewOfferRequested) {
      banner = this.renderNewOfferRequested();
    } else {
      banner = this.renderExpirationNotice();
    }

    return <div className="od-offer-expiry-notice regular-container">{banner}</div>;
  }

  private renderExpirationNotice() {
    return (
      <div className="row">
        <div className="col-xs-12 col-sm-8">
          <div className="renew-notice-text">
            <p className="no-margin">
              {this.props.hasDeclineReason ? (
                <b>Thanks for considering Opendoor! This offer has been declined.</b>
              ) : (
                <b>We're sorry. This offer has expired.</b>
              )}
            </p>
            <p className="no-margin">
              But don't worry, {this.props.leadFirstName}! Getting a renewed offer is easy and free.
              One click, and we'll send one to your inbox.
            </p>
          </div>
        </div>
        <div className="col-xs-12 col-sm-4 renew-button-container">
          <button className="button transparent renew-button" onClick={this.props.requestNewOffer}>
            Request a new offer
          </button>
        </div>
      </div>
    );
  }

  private renderNewOfferRequested() {
    return (
      <div>
        <p className="no-margin">
          <b>Got it! Your renewed offer is on its way.</b>
        </p>
        <p className="no-margin">
          Keep an eye on <b>{this.props.leadEmail}</b> for your offer. If you don't see a new offer
          by the next business day, contact your Opendoor Home Expert.
        </p>
      </div>
    );
  }

  private renderFooter() {
    const { primaryBtnText, primaryBtnDisabled, secondaryBtnText, prevBtnText } = this.props;

    if (!primaryBtnText && !prevBtnText) {
      return null;
    }

    const btnCount = reduce(
      [primaryBtnText, secondaryBtnText, prevBtnText],
      (count, btnText) => (btnText ? count + 1 : count),
      0,
    );

    return (
      <div className="od-offer-nav-fixed-footer">
        <div className="container od-offer-nav-container">
          <div
            className={classNames('od-offer-nav-buttons', 'row', 'center-xs', {
              'between-md': btnCount > 1,
            })}
          >
            {prevBtnText && (
              <div className="button-group start-xs">
                <button className="button secondary" onClick={this.onPrevClick}>
                  <span
                    className={classNames({
                      'hidden-sm-down': btnCount > 2,
                    })}
                  >
                    {prevBtnText}
                  </span>
                  {btnCount > 2 && (
                    <span className="hidden-md-up">
                      <Icon name="ei-chevron-left" />
                    </span>
                  )}
                </button>
              </div>
            )}
            {(primaryBtnText || secondaryBtnText) && (
              <div className="button-group end-xs">
                {secondaryBtnText && (
                  <button className="button secondary" onClick={this.onSecondaryClick}>
                    {secondaryBtnText}
                  </button>
                )}
                {primaryBtnText && (
                  <button
                    className="button primary"
                    disabled={primaryBtnDisabled}
                    onClick={!primaryBtnDisabled && this.onNextClick}
                  >
                    {primaryBtnText}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  private handleInvitationToken() {
    const { history, location, match, setInvitationToken } = this.props;

    if (!location || !location.search) {
      return;
    }
    if (!setInvitationToken) {
      return;
    }

    const paramPairs = location.search.substring(1).split('&');
    const newParamPairs = [];
    let invitationToken = null;

    // Look for the invitation token
    paramPairs.forEach(pair => {
      const keyValue = pair.split('=');
      if (keyValue[0] !== 'invitation_token') {
        newParamPairs.push(pair);
      } else {
        invitationToken = keyValue[1];
      }
    });

    if (!invitationToken) {
      return;
    }

    // Store the invitation token in localStorage
    setInvitationToken(invitationToken, match.params.token);

    // Reset URL location
    const newLocation = { ...location, search: `?${newParamPairs.join('&')}` };
    history.replace(newLocation);
  }
}

const mapStateToProps = createSelector(
  [
    brokerageOfferFlowSelectors.brokerageOfferData,
    brokerageOfferFlowSelectors.hasDeclineReason,
    brokerageOfferFlowSelectors.isExpired,
    brokerageOfferFlowSelectors.isNewOfferRequested,
    brokerageOfferFlowSelectors.leadEmail,
    brokerageOfferFlowSelectors.leadFirstName,
  ],
  (
    brokerageOfferData: Map<string, any>,
    hasDeclineReason: boolean,
    isExpired: boolean,
    isNewOfferRequested: boolean,
    leadEmail: string,
    leadFirstName: string,
  ) => ({
    hasDeclineReason,
    hasOfferDataLoaded: !!brokerageOfferData,
    isExpired,
    isNewOfferRequested,
    leadEmail,
    leadFirstName,
  }),
);

const mapDispatchToProps = {
  requestNewOffer,
  setInvitationToken,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(OfferLayout));
